import { useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'data-fetching-client';
import PortalIdParser from 'PortalIdParser';
import styled from 'styled-components';
import UITable from 'UIComponents/table/UITable';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import ForecastSubmissionPanel from 'ui-deal-management-components-lib/components/submissionPanel/ForecastSubmissionPanel';
import { OVERVIEW_PAGE_CONTAINER_ID } from 'ui-deal-management-components-lib/constants/constants';
import NoResultsError from './NoResultsError';
import RepOverviewTableHeaders from './RepOverviewTableHeaders';
import RepOverviewTableRow from './RepOverviewTableRow';
import TotalsRow from './TotalsRow';
import { DealStageEmptyState } from '../DealStageEmptyState';
import RepDetailError from '../repDetail/RepDetailError';
import { GET_FORECAST_COLUMNS } from '../../api/dataFetchingQueries';
import { submitForecastFailed, updateForecastDisplayFromEmbeddedSubmission as updateForecastDisplayAction } from '../../actions/ForecastActions';
import { setShowSubmissionPanelFromDealTable } from '../../actions/DealsActions';
import { setDealTableFilter } from '../../actions/FiltersActions';
import { selectTeam as selectTeamAction, setDealSidebarOwnerId as setDealSidebarOwnerIdAction } from '../../actions/OverviewDataActions';
import { TEAMS } from '../../constants/OverviewConstants';
import { DEALS_TAB, GOALS_TAB } from '../../constants/OverviewConstants';
import { DEAL_STAGE, FORECAST_COLUMNS } from '../../constants/DealGroupingType';
import { DefaultTableColumns } from '../../constants/OverviewTableConstants';
import { FILTERS } from '../../constants/FiltersConstants';
import { NAME_CELL_WIDTH, ATTAINMENT_CELL_WIDTH, AMOUNT_CELL_WIDTH, COVERAGE_CELL_WIDTH, COVERAGE_RATIO_CELL_WIDTH, FORECAST_SUBMISSION_CELL_WIDTH } from '../../constants/RepOverviewTableColumnWidths';
import { getCompanyCurrency } from '../../selectors/multiCurrencySelectors';
import { getFilters, getPipelineFilter, getTimePeriodFilter } from '../../selectors/filtersSelectors';
import { getDealGroupingTypeFilter } from '../../selectors/filtersSelectors';
import { getFiscalOffset } from '../../selectors/portalSettings';
import { getIsOverviewDataFetching } from '../../selectors/overviewSelectors';
import { getRepOverviewTableSort } from '../../selectors/sortSelectors';
import { getIsAllPipelines, getIsDealStageView, getSelectedForecastType, getSelectedForecastTypeIsDefault } from '../../selectors/forecastSelectors';
import { getClosedDealGroupingIds, getDealRollupsCurrencyCode, getHasFilteredDeactivatedUsers, getOmittedDealGroupingIds } from '../../selectors/aggregatedOverviewDataSelectors';
import { getForecastCategoryProperty, getProperties } from '../../selectors/properties';
import { isTimePeriodCustom } from '../../utils/dateUtils';
import { deriveDefaultDealGroupings, deriveDefaultDealGroupingsOld, deriveFilteredDealGroupings, isDealStage } from '../../utils/dealGroupingUtils';
import { serializeDealRollupData } from '../../utils/rowSerializationHelper';
import { divideAndSortRows } from '../../utils/rowSortUtils';
import { trackDrillIntoTeamView, createOnTrackHandlerForSubmissionPanel, trackOpenDealSidebarFromDealCount, trackOpenGoalsSidebarFromAttainment } from '../../utils/usageTracker';
import { useFetchSpecificPipelineStages } from '../../hooks/useFetchSpecificPipelineStages';
import { getShowSubmissionPanelFromDealTable } from '../../selectors/dealsSelectors';
import { getIsUngatedToManualSubmissionOverhaul } from '../../selectors/authSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TableWrapper = styled.div.withConfig({
  displayName: "RepOverviewTable__TableWrapper",
  componentId: "sc-1ek2wmj-0"
})(["position:relative;width:100%;"]);
const RepOverviewTable = ({
  isDetailPage,
  preferredCurrency,
  showDeactivatedUserError,
  tableData,
  totalsRowData
}) => {
  var _useSelector, _data$forecastColumns;
  const dispatch = useDispatch();
  const history = useHistory();

  // Selectors
  const companyCurrency = useSelector(getCompanyCurrency);
  const closedDealGroupingIds = useSelector(getClosedDealGroupingIds);
  const dealRollupsCurrencyCode = useSelector(getDealRollupsCurrencyCode);
  const hasFilteredDeactivatedUsers = useSelector(getHasFilteredDeactivatedUsers);
  const fiscalOffset = (_useSelector = useSelector(getFiscalOffset)) !== null && _useSelector !== void 0 ? _useSelector : 0;
  const isAllPipelines = useSelector(getIsAllPipelines);
  const isDataFetching = useSelector(getIsOverviewDataFetching);
  const isDealStageView = useSelector(getIsDealStageView);
  const omittedDealGroupingIds = useSelector(getOmittedDealGroupingIds);
  const properties = useSelector(getProperties);
  const selectedForecastType = useSelector(getSelectedForecastType);
  const selectedForecastIsDefault = useSelector(getSelectedForecastTypeIsDefault);
  const sort = useSelector(getRepOverviewTableSort);
  const timePeriod = useSelector(getTimePeriodFilter);
  const filters = useSelector(getFilters);
  const dealGroupingType = useSelector(getDealGroupingTypeFilter);
  const selectedPipelines = useSelector(getPipelineFilter);
  const forecastCategoryProperty = useSelector(getForecastCategoryProperty);
  const isSubmissionPanelOpen = useSelector(getShowSubmissionPanelFromDealTable);
  const isUngatedToManualSubmissionOverhaul = useSelector(getIsUngatedToManualSubmissionOverhaul);
  const {
    data: dealStages,
    loading,
    error
  } = useFetchSpecificPipelineStages(selectedPipelines.first());
  const payloadPipelineId = dealGroupingType === DEAL_STAGE ? filters.get(FILTERS.PIPELINE).first() : FORECAST_COLUMNS;
  const {
    loading: isLoadingColumns,
    data,
    error: isColumnsError
  } = useQuery(GET_FORECAST_COLUMNS, {
    variables: {
      pipelineId: payloadPipelineId
    }
  });
  const groupingColumns = (data === null || data === void 0 || (_data$forecastColumns = data.forecastColumns) === null || _data$forecastColumns === void 0 ? void 0 : _data$forecastColumns.columns) || null;
  const [submissionPanelData, setSubmissionPanelData] = useState(null);
  const dealGroupingPropertyValues = useMemo(() => {
    if (!dealStages || !forecastCategoryProperty || loading || error) {
      return [];
    }
    return dealGroupingType === DEAL_STAGE ? dealStages : forecastCategoryProperty === null || forecastCategoryProperty === void 0 ? void 0 : forecastCategoryProperty.options;
  }, [dealGroupingType, dealStages, forecastCategoryProperty, loading, error]);
  const filteredDealGroupings = useMemo(() => {
    if (!dealGroupingPropertyValues) {
      return [];
    }
    return deriveFilteredDealGroupings(dealGroupingPropertyValues, dealGroupingType, omittedDealGroupingIds, closedDealGroupingIds);
  }, [dealGroupingPropertyValues, dealGroupingType, omittedDealGroupingIds, closedDealGroupingIds]);
  const columnOrder = useMemo(() => {
    if (!filteredDealGroupings || isLoadingColumns || isColumnsError) {
      return [];
    }
    const alwaysPresentColumns = [{
      value: DefaultTableColumns.NAME,
      label: DefaultTableColumns.NAME,
      internal: true
    }, {
      value: DefaultTableColumns.ATTAINMENT,
      label: DefaultTableColumns.ATTAINMENT,
      internal: true
    }];
    return [...alwaysPresentColumns, ...(groupingColumns !== null && groupingColumns !== void 0 ? groupingColumns : [])];
  }, [filteredDealGroupings, isLoadingColumns, isColumnsError, groupingColumns]);
  const handleRowClick = (id, isUser, isCtrlClick, isShiftClick) => {
    const ownerPath = `/owner/${id}`;
    const handleSpecialClick = (isCtrlClick || isShiftClick) && (path => isShiftClick ? window.open(path) : window.open(path, '_blank'));
    if (!handleSpecialClick) {
      const defaultGroupings = isUngatedToManualSubmissionOverhaul ? deriveDefaultDealGroupings(dealGroupingPropertyValues, omittedDealGroupingIds) : deriveDefaultDealGroupingsOld(dealGroupingPropertyValues, omittedDealGroupingIds, closedDealGroupingIds);
      void dispatch(setDealTableFilter(dealGroupingType === DEAL_STAGE ? FILTERS.DEAL_STAGE_GROUPINGS : FILTERS.FORECAST_CATEGORY_GROUPINGS, defaultGroupings, `${id}`, isDetailPage));
    }
    if (isUser) {
      return handleSpecialClick ? handleSpecialClick(`/forecasting/${PortalIdParser.get()}${ownerPath}`) : history.push(ownerPath);
    }
    trackDrillIntoTeamView(isDealStageView);
    return handleSpecialClick ? handleSpecialClick(`/forecasting/${PortalIdParser.get()}/${TEAMS}/${id}`) : (() => {
      dispatch(selectTeamAction(id));
      history.push(`/${TEAMS}/${id}`);
    })();
  };
  const handleNameCellViewDealsClick = ({
    dealSidebarId,
    isTeam
  }) => {
    const defaultGroupings = isUngatedToManualSubmissionOverhaul ? deriveDefaultDealGroupings(dealGroupingPropertyValues, omittedDealGroupingIds) : deriveDefaultDealGroupingsOld(dealGroupingPropertyValues, omittedDealGroupingIds, closedDealGroupingIds);
    void dispatch(setDealTableFilter(dealGroupingType === DEAL_STAGE ? FILTERS.DEAL_STAGE_GROUPINGS : FILTERS.FORECAST_CATEGORY_GROUPINGS, defaultGroupings, null, isDetailPage));
    // Only set the deal sidebar owner id if we're not on the detail page
    if (!isDetailPage) {
      dispatch(setDealSidebarOwnerIdAction({
        dealSidebarId,
        isTeam,
        defaultTab: DEALS_TAB
      }));
    }
  };
  const handleDealCountClick = (groupingIds, ownerId, isUser) => {
    void dispatch(setDealTableFilter(dealGroupingType === DEAL_STAGE ? FILTERS.DEAL_STAGE_GROUPINGS : FILTERS.FORECAST_CATEGORY_GROUPINGS, groupingIds, `${ownerId}`, isDetailPage));
    // Only set the deal sidebar owner id if we're not on the detail page
    if (!isDetailPage) {
      dispatch(setDealSidebarOwnerIdAction({
        dealSidebarId: ownerId,
        isTeam: !isUser,
        defaultTab: DEALS_TAB
      }));
    }
    trackOpenDealSidebarFromDealCount({
      isDealStageView,
      isDetail: isDetailPage
    });
  };
  const handleAttainmentBarClick = (groupingIds, ownerId, isUser) => {
    void dispatch(setDealTableFilter(dealGroupingType === DEAL_STAGE ? FILTERS.DEAL_STAGE_GROUPINGS : FILTERS.FORECAST_CATEGORY_GROUPINGS, groupingIds, `${ownerId}`, isDetailPage));
    // Only set the deal sidebar owner id if we're not on the detail page
    if (!isDetailPage) {
      dispatch(setDealSidebarOwnerIdAction({
        dealSidebarId: ownerId,
        isTeam: !isUser,
        defaultTab: GOALS_TAB
      }));
    }
    trackOpenGoalsSidebarFromAttainment({
      isDealStageView,
      isDetail: isDetailPage
    });
  };
  const handleEditForecastClick = forecastData => {
    if (isUngatedToManualSubmissionOverhaul && isDetailPage && !isSubmissionPanelOpen) {
      // Open rep detail version of submission panel
      dispatch(setShowSubmissionPanelFromDealTable(true));
    } else {
      setSubmissionPanelData(forecastData);
    }
  };
  const handleSubmitSuccess = ({
    amount,
    prevAmount,
    submittedPipelineId,
    cadence,
    timePeriodValue,
    isAllPipelinesSubmission
  }) => {
    const {
      id,
      isUser
    } = submissionPanelData || {};
    dispatch(updateForecastDisplayAction({
      amount,
      cadence,
      fiscalOffset,
      id,
      isUser,
      prevAmount,
      submittedPipelineId,
      timePeriodValue,
      isAllPipelinesSubmission
    }));
    setSubmissionPanelData(null);
  };
  const renderTotalsRow = () => {
    const serializedDealRollupData = serializeDealRollupData(totalsRowData, filteredDealGroupings, dealRollupsCurrencyCode, companyCurrency);
    const userCount = 0;
    return /*#__PURE__*/_jsx(TotalsRow, {
      closedDealGroupingIds: closedDealGroupingIds,
      columns: columnOrder,
      dealRollupData: serializedDealRollupData,
      dealRollupGroupings: filteredDealGroupings,
      onClickAttainmentBar: handleAttainmentBarClick,
      onDealCountClick: handleDealCountClick,
      onEditForecastClick: handleEditForecastClick,
      onNameCellViewDealsClick: handleNameCellViewDealsClick,
      rowData: totalsRowData,
      userCount: userCount
    });
  };
  const renderTableBody = () => {
    const sortedRows = divideAndSortRows(tableData, sort);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [!isDetailPage && renderTotalsRow(), sortedRows.map(rowData => {
        const serializedDealRollupData = serializeDealRollupData(rowData, filteredDealGroupings, dealRollupsCurrencyCode, companyCurrency);
        const {
          id,
          userCount,
          isUser
        } = rowData;
        const teamHasMembers = (userCount !== null && userCount !== void 0 ? userCount : 0) > 0;
        if (isUser || teamHasMembers) {
          return /*#__PURE__*/_jsx(RepOverviewTableRow, {
            rowData: rowData,
            dealRollupData: serializedDealRollupData,
            dealRollupGroupings: filteredDealGroupings,
            isDetailPage: isDetailPage,
            onRowClick: handleRowClick,
            onAttainmentBarClick: handleAttainmentBarClick,
            onDealCountClick: handleDealCountClick,
            onNameCellViewDealsClick: handleNameCellViewDealsClick,
            onEditForecastClick: handleEditForecastClick,
            columns: columnOrder,
            closedDealGroupingIds: closedDealGroupingIds
          }, id);
        }
        return null;
      })]
    });
  };
  const getColGroupWidth = column => {
    switch (column) {
      case DefaultTableColumns.NAME:
        return NAME_CELL_WIDTH;
      case DefaultTableColumns.ATTAINMENT:
        return ATTAINMENT_CELL_WIDTH;
      case DefaultTableColumns.COVERAGE:
        return COVERAGE_CELL_WIDTH;
      case DefaultTableColumns.COVERAGE_RATIO:
        return COVERAGE_RATIO_CELL_WIDTH;
      case DefaultTableColumns.FORECAST:
        return FORECAST_SUBMISSION_CELL_WIDTH;
      default:
        return AMOUNT_CELL_WIDTH;
    }
  };
  const getShouldShowNoDataMessage = () => {
    const hasData = tableData && tableData.size > 0;
    return !isDetailPage && !isDataFetching && !hasData && hasFilteredDeactivatedUsers;
  };
  const renderColGroup = () => {
    const hasColumn = column => column === DefaultTableColumns.COVERAGE || column === DefaultTableColumns.COVERAGE_RATIO || column === DefaultTableColumns.FORECAST || (filteredDealGroupings === null || filteredDealGroupings === void 0 ? void 0 : filteredDealGroupings.find(grouping => isDealStage(grouping) ? grouping.stageId === column : grouping.value === column));
    return /*#__PURE__*/_jsxs("colgroup", {
      children: [/*#__PURE__*/_jsx("col", {
        style: {
          width: getColGroupWidth(DefaultTableColumns.NAME)
        }
      }), /*#__PURE__*/_jsx("col", {
        style: {
          width: getColGroupWidth(DefaultTableColumns.ATTAINMENT)
        }
      }), columnOrder.map(column => hasColumn(column.value) && /*#__PURE__*/_jsx("col", {
        style: {
          width: getColGroupWidth(column.value)
        }
      }, `${column.value}${column.internal ? '-internal' : ''}`))]
    });
  };
  const {
    id,
    isUser
  } = submissionPanelData || {};
  const hasData = tableData.size > 0;
  const showNoDataState = getShouldShowNoDataMessage();
  const showEmptyState = !showDeactivatedUserError && !hasData && !isDataFetching && !showNoDataState;
  const timePeriodForSubmissionPanel = !isTimePeriodCustom(timePeriod) ? timePeriod : undefined;
  const handleClose = useCallback(() => {
    setSubmissionPanelData(null);
  }, []);
  const handleFailure = useCallback(errorType => {
    dispatch(submitForecastFailed(errorType));
  }, [dispatch]);
  const submissionPanel = /*#__PURE__*/_jsx(ForecastSubmissionPanel, {
    containerId: OVERVIEW_PAGE_CONTAINER_ID,
    dealProperties: properties,
    forecastType: selectedForecastType,
    ownerId: isUser ? id : undefined,
    teamId: isUser ? undefined : id,
    pipelineId: isAllPipelines ? undefined : filters.get(FILTERS.PIPELINE).first(),
    timePeriod: timePeriodForSubmissionPanel,
    isDealStageView: isDealStageView,
    onSubmitSuccess: handleSubmitSuccess,
    onSubmitFailure: handleFailure,
    onClose: handleClose,
    onTrack: createOnTrackHandlerForSubmissionPanel({
      companyCurrency,
      currencyCode: preferredCurrency || companyCurrency,
      isDealStageView,
      isDetailPage,
      submissionPanelData: submissionPanelData,
      isDefaultForecast: selectedForecastIsDefault
    })
  });
  if (!filteredDealGroupings || !columnOrder.length) {
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(UILoadingOverlay, {
        contextual: true,
        "data-test-id": "overview-table-loading"
      })
    });
  }
  return /*#__PURE__*/_jsxs(TableWrapper, {
    children: [isDataFetching && /*#__PURE__*/_jsx(UILoadingOverlay, {
      contextual: true,
      "data-test-id": "overview-table-loading"
    }), /*#__PURE__*/_jsxs(UITable, {
      "data-test-id": "rep-overview-table",
      style: {
        tableLayout: 'fixed',
        width: 'min-content'
      },
      children: [renderColGroup(), /*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx(RepOverviewTableHeaders, {
          dealRollupGroupings: filteredDealGroupings,
          columns: columnOrder,
          groupingType: dealGroupingType
        })
      }), hasData && /*#__PURE__*/_jsx(NavMarker, {
        name: "OVERVIEW_TABLE_LOADED",
        children: /*#__PURE__*/_jsx("tbody", {
          children: renderTableBody()
        })
      })]
    }), showNoDataState && /*#__PURE__*/_jsx(NoResultsError, {}), showDeactivatedUserError && /*#__PURE__*/_jsx(NavMarker, {
      name: "OVERVIEW_TABLE_DEACTIVATED_USER_ERROR_STATE",
      children: /*#__PURE__*/_jsx(RepDetailError, {
        isDealStageView: isDealStageView
      })
    }), showEmptyState && /*#__PURE__*/_jsx(NavMarker, {
      name: "OVERVIEW_TABLE_EMPTY_STATE",
      children: /*#__PURE__*/_jsx(DealStageEmptyState, {})
    }), submissionPanelData && submissionPanel]
  });
};
export default RepOverviewTable;